import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Target, BookOpen, Calculator, Pencil, Hash, Microscope,
  Heart, Loader2, Save, AlertTriangle, Star
} from 'lucide-react';

const FocusAreasSelector = ({ childId, childName, onUpdate }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  
  const [focusData, setFocusData] = useState({
    focus_subjects: [],
    focus_modules: [],
    priority_level: 'normal',
    notes: ''
  });

  const subjects = [
    { id: 'reading', name: 'Reading/ELA', description: 'EL Education curriculum for literacy', icon: BookOpen, color: 'bg-blue-100 text-blue-600' },
    { id: 'math', name: 'Math', description: 'Envision Mathematics curriculum', icon: Calculator, color: 'bg-green-100 text-green-600' },
    { id: 'alphabets', name: 'Alphabets', description: 'Letter recognition and sounds', icon: Pencil, color: 'bg-purple-100 text-purple-600' },
    { id: 'counting', name: 'Counting', description: 'Number sense and counting skills', icon: Hash, color: 'bg-orange-100 text-orange-600' },
    { id: 'writing', name: 'Writing', description: 'Handwriting and composition', icon: Pencil, color: 'bg-pink-100 text-pink-600' },
    { id: 'science', name: 'Science', description: 'Natural world exploration', icon: Microscope, color: 'bg-teal-100 text-teal-600' },
    { id: 'christian', name: 'Christian/Bible', description: 'Bible stories and faith learning', icon: Heart, color: 'bg-amber-100 text-amber-600' },
  ];

  useEffect(() => {
    if (open) {
      fetchFocusAreas();
    }
  }, [open, childId]);

  const fetchFocusAreas = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/children/${childId}/focus`);
      setFocusData({
        focus_subjects: response.data.focus_subjects || [],
        focus_modules: response.data.focus_modules || [],
        priority_level: response.data.priority_level || 'normal',
        notes: response.data.notes || ''
      });
    } catch (error) {
      console.error('Failed to fetch focus areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubject = (subjectId) => {
    setFocusData(prev => ({
      ...prev,
      focus_subjects: prev.focus_subjects.includes(subjectId)
        ? prev.focus_subjects.filter(s => s !== subjectId)
        : [...prev.focus_subjects, subjectId]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/children/${childId}/focus`, focusData);
      toast.success('Focus areas updated!');
      setOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update focus areas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid={`focus-btn-${childId}`}>
          <Target className="w-4 h-4" />
          Focus Areas
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-500" />
            Set Focus Areas for {childName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : (
          <div className="space-y-6 pt-4">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Star className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Prioritize Learning</p>
                  <p className="text-sm text-blue-600">
                    Select subjects you want {childName} to focus on. These will appear first 
                    in their curriculum and be highlighted during learning sessions.
                  </p>
                </div>
              </div>
            </div>

            {/* Subject Selection */}
            <div>
              <Label className="text-base font-semibold mb-4 block">Select Focus Subjects</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                {subjects.map(subject => (
                  <div
                    key={subject.id}
                    onClick={() => toggleSubject(subject.id)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      focusData.focus_subjects.includes(subject.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                    data-testid={`focus-subject-${subject.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subject.color}`}>
                      <subject.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{subject.name}</p>
                      <p className="text-xs text-slate-500">{subject.description}</p>
                    </div>
                    <Checkbox 
                      checked={focusData.focus_subjects.includes(subject.id)}
                      className="pointer-events-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Priority Level */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Priority Level</Label>
              <div className="flex gap-3">
                <Button
                  variant={focusData.priority_level === 'normal' ? 'default' : 'outline'}
                  onClick={() => setFocusData(prev => ({ ...prev, priority_level: 'normal' }))}
                  className={focusData.priority_level === 'normal' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  data-testid="priority-normal"
                >
                  Normal
                </Button>
                <Button
                  variant={focusData.priority_level === 'high' ? 'default' : 'outline'}
                  onClick={() => setFocusData(prev => ({ ...prev, priority_level: 'high' }))}
                  className={focusData.priority_level === 'high' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  data-testid="priority-high"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  High Priority
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                High priority will add extra reminders and suggest these subjects first.
              </p>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Notes (Optional)</Label>
              <Textarea
                placeholder="Any specific goals or areas to work on..."
                value={focusData.notes}
                onChange={(e) => setFocusData(prev => ({ ...prev, notes: e.target.value }))}
                className="h-24"
                data-testid="focus-notes"
              />
            </div>

            {/* Summary */}
            {focusData.focus_subjects.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700">Current Focus:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {focusData.focus_subjects.map(subjectId => {
                    const subject = subjects.find(s => s.id === subjectId);
                    return (
                      <span
                        key={subjectId}
                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                      >
                        {subject?.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="save-focus-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Focus Areas
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FocusAreasSelector;
